import ApiRequest from "@/common/lib/apiRequest";
import { hexlify } from "@/common/lib/utils";
import BN from "bignumber.js";
const mutationTypes = {
  SET_OLD_BALANCES: "migration/SET_OLD_BALANCES",
  SET_MIGRATION: "migration/SET_MIGRATION",
  SET_UNCLAIMED_REWARDS: "migration/SET_UNCLAIMED_REWARDS",
};

const state = {
  oldBalances: {},
  unclaimedRewards: [],
  shouldShowMigration: false,
};

const actions = {
  async fetchOldBalances({ commit, rootGetters }) {
    const address = rootGetters["userWallets/walletSelected"].address;
    let [{ value: available }, { value: staked }] = await Promise.all([
      ApiRequest.get("/kyberdao/legacy/staker/available_balance/" + address),
      ApiRequest.get("/kyberdao/legacy/staker/next_staked_balance/" + address),
    ]);
    if (available < 1e-6) available = 0;
    if (staked < 1e-6) staked = 0;
    const total = new BN(available).plus(staked).valueOf();
    commit(mutationTypes.SET_OLD_BALANCES, { available, staked, total });
  },
  async checkMigrationStatus({ state, commit, dispatch }) {
    await Promise.all([
      dispatch("fetchOldBalances"),
      dispatch("fetchUnclaimedRewards"),
    ]);
    if (state.oldBalances.total > 0 || state.unclaimedRewards.value > 0) {
      commit(mutationTypes.SET_MIGRATION, true);
    }
  },
  async hideMigration({ commit }) {
    commit(mutationTypes.SET_MIGRATION, false);
  },
  async getUnstakeRawTx({ getters, rootGetters, dispatch }) {
    const maxPriorityFeePerGas = rootGetters["appConfig/maxPriorityFeePerGas"];
    const stakedAmount = getters.oldBalances.staked;
    const address = rootGetters["userWallets/walletSelected"].address;
    const result = await ApiRequest.get(
      `/kyberdao/legacy/staking/digest/withdraw/${address}`,
      {
        params: {
          amount: stakedAmount,
        },
      }
    );
    const [{ value: nonce }, { fast }] = await Promise.all([
      ApiRequest.get(`/kyberdao/network/nonce/${address}`),
      dispatch("appConfig/fetchEthGasPrice", null, { root: true }),
    ]);
    const tx = {
      from: address,
      to: result.contractAddress,
      maxFeePerGas: hexlify(fast * 1e9),
      maxPriorityFeePerGas: hexlify(maxPriorityFeePerGas),
      gas: hexlify(result.gasLimit),
      value: "0x0",
      nonce: hexlify(nonce),
      data: result.digest,
    };
    return tx;
  },
  async getOldKncAllowance({ commit, rootGetters }) {
    const address = rootGetters["userWallets/walletSelected"].address;
    const result = await ApiRequest.get(
      `/kyberdao/staker/allowance_old_knc/${address}`
    );
    return Number(result.value);
  },
  async getOldKncApproveRawTx({ rootGetters, dispatch }) {
    const maxPriorityFeePerGas = rootGetters["appConfig/maxPriorityFeePerGas"];
    const address = rootGetters["userWallets/walletSelected"].address;
    const result = await ApiRequest.get(
      `/kyberdao/staking/digest/approve_old_knc/${address}`
    );
    const [{ value: nonce }, { fast }] = await Promise.all([
      ApiRequest.get(`/kyberdao/network/nonce/${address}`),
      dispatch("appConfig/fetchEthGasPrice", null, { root: true }),
    ]);
    const tx = {
      from: address,
      to: result.contractAddress,
      maxFeePerGas: hexlify(fast * 1e9),
      maxPriorityFeePerGas: hexlify(maxPriorityFeePerGas),
      gas: hexlify(result.gasLimit),
      value: "0x0",
      nonce: hexlify(nonce),
      data: result.digest,
    };
    return tx;
  },
  async getStakeRawTx({ getters, rootGetters, dispatch }, allowance) {
    const maxPriorityFeePerGas = rootGetters["appConfig/maxPriorityFeePerGas"];
    const address = rootGetters["userWallets/walletSelected"].address;
    let amount = getters.oldBalances.available;
    allowance = new BN(allowance).dividedBy(1e18).valueOf();
    if (new BN(amount).isGreaterThan(allowance)) {
      amount = allowance;
    }
    const result = await ApiRequest.get(
      `/kyberdao/staking/digest/deposit_old_knc/${address}`,
      {
        params: {
          amount,
        },
      }
    );
    const [{ value: nonce }, { fast }] = await Promise.all([
      ApiRequest.get(`/kyberdao/network/nonce/${address}`),
      dispatch("appConfig/fetchEthGasPrice", null, { root: true }),
    ]);
    const tx = {
      from: address,
      to: result.contractAddress,
      maxFeePerGas: hexlify(fast * 1e9),
      maxPriorityFeePerGas: hexlify(maxPriorityFeePerGas),
      gas: hexlify(result.gasLimit),
      value: "0x0",
      nonce: hexlify(nonce),
      data: result.digest,
    };
    return tx;
  },
  async fetchUnclaimedRewards({ commit, rootGetters }) {
    const address = rootGetters["userWallets/walletSelected"].address;
    const rewards = await ApiRequest.get(
      `/kyberdao/legacy/staker/unclaim_rewards/${address}`
    );
    let value = rewards.reduce(
      (acc, cur) => new BN(acc).plus(cur.value).toNumber(),
      0
    );
    if (value < 1e-6) value = 0;
    commit(mutationTypes.SET_UNCLAIMED_REWARDS, { value, rewards });
  },
  async getClaimRewardsRawTx({ rootGetters, dispatch }, payload) {
    const maxPriorityFeePerGas = rootGetters["appConfig/maxPriorityFeePerGas"];
    const address = rootGetters["userWallets/walletSelected"].address;
    const result = await ApiRequest.get(
      `/kyberdao/legacy/staking/digest/claim_reward/${address}`,
      { params: { epoch_group: payload.epochGroup.join(",") } }
    );
    const [{ value: nonce }, { fast }] = await Promise.all([
      ApiRequest.get(`/kyberdao/network/nonce/${address}`),
      dispatch("appConfig/fetchEthGasPrice", null, { root: true }),
    ]);
    const tx = {
      from: address,
      to: result.contractAddress,
      maxFeePerGas: hexlify(fast * 1e9),
      maxPriorityFeePerGas: hexlify(maxPriorityFeePerGas),
      gas: hexlify(result.gasLimit),
      value: "0x0",
      nonce: hexlify(nonce),
      data: result.digest,
    };
    return tx;
  },
};

const mutations = {
  [mutationTypes.SET_OLD_BALANCES](state, data) {
    state.oldBalances = data;
  },
  [mutationTypes.SET_MIGRATION](state, value) {
    state.shouldShowMigration = value;
  },
  [mutationTypes.SET_UNCLAIMED_REWARDS](state, value) {
    state.unclaimedRewards = value;
  },
};

const getters = {
  oldBalances: state => state.oldBalances,
  shouldShowMigration: state => state.shouldShowMigration,
  unclaimedRewards: state => state.unclaimedRewards,
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
